import { useContext } from 'react';
import { MobXProviderContext } from 'mobx-react';
import BlogStore from '@portal/env/stores/blogStore';
import {
  keepPreviousData,
  queryOptions,
  useQuery,
} from '@tanstack/react-query';
import { queryKeys } from '@portal/ui/hooks/queryKeys';
import { IValueMap } from 'mobx-react/src/types/IValueMap';
import {
  fetchBannerBlogPosts,
  fetchBlogPostsPage,
  type BlogPostsParams,
} from '@portal/env/hooks/fetchBlogs';
import { useAuth } from './useAuth';

const MAX_FEATURED_BLOGS = 3;

// The user ID keys the cache per user and, unlike the token, survives token refreshes
export type BlogsAuth = {
  token: string;
  host: string;
  userId: number | null;
};

// One Nexudus page per request: paging, category and search stay server-side
export const blogPostsQueryOptions = (
  { token, host, userId }: BlogsAuth,
  params: BlogPostsParams,
) =>
  queryOptions({
    queryKey: queryKeys.blogPostsList({ userId, host, ...params }),
    enabled: !!token,
    queryFn: () => fetchBlogPostsPage(token, host, params),
    // Keep showing the current page while the next one loads
    placeholderData: keepPreviousData,
  });

// Banner posts come from their own Nexudus resource, not a page of the blog
// listing, so featured posts beyond the newest few aren't missed
export const featuredBlogsQueryOptions = ({ token, host, userId }: BlogsAuth) =>
  queryOptions({
    queryKey: queryKeys.featuredBlogs({ userId, host }),
    enabled: !!token,
    queryFn: () => fetchBannerBlogPosts(token, host),
    select: (data) => data.slice(0, MAX_FEATURED_BLOGS),
  });

const useBlogsAuth = (): BlogsAuth => {
  const { accessToken, host, customer } = useAuth();
  return { token: accessToken, host, userId: customer?.Id ?? null };
};

export const useBlogPostsQuery = (params: BlogPostsParams) =>
  useQuery(blogPostsQueryOptions(useBlogsAuth(), params));

// Not a suspense query: useSuspenseQuery ignores `enabled` and throws errors,
// and there is no error boundary. Callers render nothing while loading or on error.
export const useFeaturedBlogsQuery = () =>
  useQuery(featuredBlogsQueryOptions(useBlogsAuth()));

export function useBlogs() {
  const stores = useContext(MobXProviderContext) as IValueMap;
  if (!stores?.blogStore || !stores?.authStore) {
    throw new Error('useBlogs must be used within a MobX Provider');
  }

  return { store: stores.blogStore as BlogStore };
}
